import axios, { AxiosInstance } from 'axios'
import { DEFAULT_PAGE_SIZE } from '@zocks-communications/client-sdk-js'
import { readToken, saveToken, Token } from '../common/token'
import { refreshLock } from '../common/baseQuery'
import { refreshTokenRequest } from './auth'
import { tokenRefreshed } from '../store/auth/authSlice'
import { useAppDispatch } from '../store/hooks'
import { Mutex } from 'async-mutex'

export const authHttpClient = axios.create({
  baseURL: import.meta.env.REACT_APP_AUTH_API_BASE_URL!,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

export const authenticatedAuthHttpClient = axios.create({
  baseURL: `${import.meta.env.REACT_APP_AUTH_API_BASE_URL!}`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

const addInterceptors = (httpClient: AxiosInstance) => {
  addDataExtractInterceptor(httpClient)
  addReactRefreshTokenInterceptor(httpClient)
  addAppInfoRequestInterceptor(httpClient)
}

const addReactRefreshTokenInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.request.use(
    async (requestConfig) => {
      if (!requestConfig.headers) {
        requestConfig.headers = {}
      }

      let token = readToken()
      if (token) {
        if (token.isAccessTokenExpired && token.isRenewable) {
          await refreshLock(async () => {
            const dispatch = useAppDispatch()
            const response = await refreshTokenRequest(token!.accountId, token!.refreshToken!)
            dispatch(tokenRefreshed(response))
          })
          token = readToken()!
        }
        requestConfig.headers['Authorization'] = `Bearer ${token.accessToken}`
      }
      return requestConfig
    },
    (error) => {
      console.error(error)
    },
  )
}

const basicMutex = new Mutex()

export const addBasicRefreshTokenInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.request.use(
    async (requestConfig) => {
      if (!requestConfig.headers) {
        requestConfig.headers = {}
      }

      let token = readToken()
      if (token) {
        if (token.isAccessTokenExpired && token.isRenewable) {
          await refreshLock(async () => {
            const response = await refreshTokenRequest(token!.accountId, token!.refreshToken!)
            token = Token.fromPayload(response)
            saveToken(token)
          }, basicMutex)
          token = readToken()!
        }
        requestConfig.headers['Authorization'] = `Bearer ${token.accessToken}`
      }
      return requestConfig
    },
    (error) => {
      console.error(error)
    },
  )
}

const addAppInfoRequestInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.request.use(
    (requestConfig) => {
      if (!requestConfig.headers) {
        requestConfig.headers = {}
      }
      requestConfig.headers['X-App-Info'] = 'ZocksConsole'
      return requestConfig
    },
    (error) => {
      console.error(error)
    },
  )
}

const addDataExtractInterceptor = (httpClient: AxiosInstance) => {
  httpClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
      return Promise.reject({
        errorCode: error.code,
        statusCode: error.response.status,
        ...error.response.data,
      })
    },
  )
}

;[authenticatedAuthHttpClient].forEach(addInterceptors)

addDataExtractInterceptor(authHttpClient)
addAppInfoRequestInterceptor(authHttpClient)

export const getPagedData = async <T>(client: AxiosInstance, path: string, params?: { [key: string]: any }, pageSize = DEFAULT_PAGE_SIZE): Promise<T[]> => {
  const allData: T[] = []
  let page = 0
  let total = 1
  while (page < total) {
    const response = await client.get(path, { params: { ...params, page, size: pageSize } })
    const {
      data,
      page: { totalPages },
    } = response.data
    allData.push(...data)
    total = totalPages
    page = page + 1
  }
  return allData
}
